import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_FILE_ERRORS, chatFileKind, chatFiles, checkChatFiles, ChatFileRejection, type ChatFileKind, type Message } from '@jerome2/shared';
import { ApiError } from '../api.ts';
import { createStore, useStore } from '../lib/store.ts';

// De bestanden bij het bericht in opbouw (slepen, plakken of kiezen): de lijst boven het vak, met een miniatuur
// van een afbeelding, de grootte en bij een weigering de vaste zin. Vooraf weigert het scherm op soort, grootte, aantal en
// som (dezelfde regels als de server, shared/src/files.ts); weigert de server een bestand bij versturen, dan krijgt dat
// bestand de vaste zin met het kenmerk uit het serverlog. Alleen bestanden zonder weigering gaan mee. Versturen neemt
// de bestanden van dat moment (`ready`) en haalt na afloop alleen die weg (`drop`): wat er tijdens het versturen bij
// komt, hoort bij het volgende bericht. Komt een bericht met bestanden in de wachtrij, dan onthoudt de lade ze
// (`remember`), zodat Stop of het kruisje ze samen met de tekst terugzet (`restore`). Slepen of plakken in de gesprekkenkolom
// van de schil (desktop) opent een nieuw gesprek: de kolom legt de bestanden klaar voor dat chattabblad (`handFiles`) en
// het chatscherm neemt ze over zodra het er staat (`useHandedFiles`).

export interface PendingFile {
  key: string;
  file: File;
  name: string;
  size: number;
  kind: ChatFileKind | null;
  /** De vaste zin bij een weigering (met het kenmerk als de server weigerde), anders null. */
  error: string | null;
  /** Een voorvertoning van een afbeelding (object-URL), anders null. */
  preview: string | null;
}

export interface ChatFiles {
  items: readonly PendingFile[];
  add(files: readonly File[]): void;
  remove(key: string): void;
  clear(): void;
  /** De bestanden die meegaan: alles zonder weigering, in volgorde, met hun sleutel voor `drop` en `rejected`. */
  ready(): PendingFile[];
  /** Haalt verstuurde bestanden uit de lade, op sleutel; wat er intussen bij kwam blijft staan. */
  drop(keys: readonly string[]): void;
  /** Verwerkt een fout bij versturen; geeft de vaste zin (met kenmerk) als het een weigering van een bestand was, anders null.
   * `sent` zijn de bestanden die meegingen, zodat de plaats uit het antwoord bij het juiste bestand komt (zonder `sent`: alles
   * zonder weigering van dit moment). Kon de server een bestand niet bewaren (failed), dan blijft het ongemarkeerd staan voor
   * een nieuwe poging. */
  rejected(e: unknown, sent?: readonly PendingFile[]): string | null;
  /** Onthoudt de bestanden van een bericht dat in de wachtrij kwam. */
  remember(messageId: string, files: readonly File[]): void;
  /** Zet de bestanden van weggehaalde wachtende berichten terug in de lade; geeft de namen die niet meer terug te zetten
   * waren (het bericht kwam van een ander apparaat of van vóór een herlaadbeurt). */
  restore(removed: readonly Message[]): string[];
}

/** Bestanden die buiten het chatscherm binnenkwamen (de gesprekkenkolom van de schil), per chattabblad, tot het scherm ze overneemt. */
export const handedFiles = createStore<Record<string, File[]>>({});

/** Legt bestanden klaar voor de lade van een chattabblad; wat er al klaarlag, blijft erbij. */
export function handFiles(tabId: string, files: readonly File[]): void {
  if (!files.length) return;
  handedFiles.set((s) => ({ ...s, [tabId]: [...(s[tabId] ?? []), ...files] }));
}

/** Neemt de bestanden over die voor dit tabblad klaarliggen, één keer (ook als het scherm er al stond). */
export function useHandedFiles(tabId: string, add: (files: readonly File[]) => void): void {
  const waiting = useStore(handedFiles, (s) => s[tabId]);
  useEffect(() => {
    // Uit de store zelf, niet uit `waiting`: een tweede aanroep van dit effect (StrictMode) vindt dan niets meer.
    const list = handedFiles.get()[tabId];
    if (!list?.length) return;
    handedFiles.set((s) => {
      const rest = { ...s };
      delete rest[tabId];
      return rest;
    });
    add(list);
  }, [waiting, tabId, add]);
}

/** Een geplakte schermafbeelding heet in de browser meestal "image.png": geef hem een naam met de tijd. */
export function pastedName(file: File, now: Date = new Date()): string {
  if (file.name && file.name !== 'image.png' && file.name !== 'blob') return file.name;
  const t = [now.getHours(), now.getMinutes(), now.getSeconds()].map((n) => String(n).padStart(2, '0')).join('.');
  const ext = file.type === 'image/jpeg' ? 'jpg' : (file.type.split('/')[1] ?? 'png').replace(/[^a-z0-9]/g, '') || 'png';
  return `Geplakte afbeelding ${t}.${ext}`;
}

/** De bestanden uit het klembord of een sleepactie; ook als de browser ze alleen als `items` geeft. */
export function filesFrom(data: DataTransfer | null, now: Date = new Date()): File[] {
  if (!data) return [];
  let list = Array.from(data.files ?? []);
  if (!list.length && data.items) list = Array.from(data.items).flatMap((i) => (i.kind === 'file' ? [i.getAsFile()].filter((f): f is File => f !== null) : []));
  return list.map((f) => (f.name && f.name !== 'image.png' && f.name !== 'blob' ? f : new File([f], pastedName(f, now), { type: f.type })));
}

/** De bestanden van een plakactie. Excel, Numbers, Word en PowerPoint zetten naast de gekopieerde cellen of tekst ook een
 * afbeelding ervan op het klembord; dan wil Jeroen de tekst en plakt die zoals altijd. Een schermafbeelding of een gekopieerd
 * bestand draagt geen opgemaakte tekst (html of rtf) en komt als bestand bij het bericht. */
export function pastedFiles(data: DataTransfer | null, now: Date = new Date()): File[] {
  if (!data) return [];
  const types = Array.from(data.types ?? []);
  const text = types.includes('text/plain') ? data.getData('text/plain').trim() : '';
  if (text && (types.includes('text/html') || types.includes('text/rtf'))) return [];
  return filesFrom(data, now);
}

/** Of een sleepactie bestanden draagt (en niet bijvoorbeeld tekst of een link). */
export function dragHasFiles(data: DataTransfer | null): boolean {
  return Boolean(data && Array.from(data.types ?? []).includes('Files'));
}

interface Entry {
  key: string;
  file: File;
  preview: string | null;
  /** Weigering door de server bij de laatste poging, met kenmerk. */
  serverError: string | null;
}

/** Zoveel wachtende berichten met bestanden onthoudt de lade (de oudste valt af). */
const REMEMBER_MAX = 20;

let seq = 0;
const objectUrl = (file: File): string | null => (file.type.startsWith('image/') && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null);
const revoke = (url: string | null) => {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
};

export function useChatFiles(): ChatFiles {
  const [entries, setEntries] = useState<Entry[]>([]);
  const live = useRef(entries);
  live.current = entries;
  // Weg van het scherm: de voorvertoningen vrijgeven.
  useEffect(() => () => live.current.forEach((e) => revoke(e.preview)), []);
  const remembered = useRef(new Map<string, File[]>());

  const items = useMemo<PendingFile[]>(() => {
    const problems = checkChatFiles(entries.map((e) => ({ name: e.file.name, type: e.file.type, size: e.file.size })));
    return entries.map((e, i) => {
      const problem = problems[i];
      return { key: e.key, file: e.file, name: e.file.name, size: e.file.size, kind: chatFileKind(e.file.name, e.file.type), error: problem ? CHAT_FILE_ERRORS[problem] : e.serverError, preview: e.preview };
    });
  }, [entries]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const add = useCallback((files: readonly File[]) => {
    if (!files.length) return;
    setEntries((list) => [...list, ...files.map((file) => ({ key: `f${++seq}`, file, preview: objectUrl(file), serverError: null }))]);
  }, []);
  const remove = useCallback((key: string) => {
    setEntries((list) => {
      revoke(list.find((e) => e.key === key)?.preview ?? null);
      return list.filter((e) => e.key !== key);
    });
  }, []);
  const drop = useCallback((keys: readonly string[]) => {
    if (!keys.length) return;
    const gone = new Set(keys);
    setEntries((list) => {
      list.filter((e) => gone.has(e.key)).forEach((e) => revoke(e.preview));
      return list.filter((e) => !gone.has(e.key));
    });
  }, []);
  const clear = useCallback(() => {
    setEntries((list) => {
      list.forEach((e) => revoke(e.preview));
      return [];
    });
  }, []);
  const ready = useCallback(() => itemsRef.current.filter((i) => !i.error), []);
  const rejected = useCallback((e: unknown, sent?: readonly PendingFile[]): string | null => {
    if (!(e instanceof ApiError) || !e.data) return null;
    const r = ChatFileRejection.safeParse(e.data);
    if (!r.success) return null;
    // Altijd de eigen vaste zin, nooit de tekst die de server meestuurde.
    const sentence = `${CHAT_FILE_ERRORS[r.data.problem]} (kenmerk ${r.data.kenmerk})`;
    const list = sent ?? itemsRef.current.filter((i) => !i.error);
    const target = r.data.index >= 0 ? list[r.data.index] : undefined;
    // Niet bewaard (een haperende database): het bestand zelf mankeert niets, dus het blijft klaarstaan voor een nieuwe poging.
    if (target && r.data.problem !== 'failed') setEntries((entries) => entries.map((x) => (x.key === target.key ? { ...x, serverError: sentence } : x)));
    return sentence;
  }, []);
  const remember = useCallback((messageId: string, files: readonly File[]) => {
    if (!files.length) return;
    const map = remembered.current;
    map.set(messageId, [...files]);
    while (map.size > REMEMBER_MAX) map.delete(map.keys().next().value as string);
  }, []);
  const restore = useCallback((removed: readonly Message[]): string[] => {
    const missing: string[] = [];
    const back: File[] = [];
    for (const m of removed) {
      const refs = chatFiles(m);
      if (!refs.length) continue;
      const files = remembered.current.get(m.id);
      if (files) {
        remembered.current.delete(m.id);
        back.push(...files);
      } else missing.push(...refs.map((r) => r.name));
    }
    add(back);
    return missing;
  }, [add]);
  return { items, add, remove, clear, ready, drop, rejected, remember, restore };
}
